import { useMemo, useState, type JSX } from 'react';
import { Download, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { useUrlState } from '@/hooks/useUrlState';
import { useFolders } from '../hooks/folders';
import { OutreachStatusBadge } from '../components/OutreachStatus';
import type { OutreachState } from '../api/types';
import { placeName } from '../search/params';
import { useAudienceRows, useAudienceSummary } from '../hooks/email';
import { PLATFORM_LABELS, PLATFORMS, profileUrl, type Platform } from '../lib/platform';
import { CheckboxField, DebouncedInput, Field, Note, Segmented } from '../workspace/ui';
import {
  ACCOUNT_TYPE_LABELS,
  EXCLUSION_LABELS,
  FACE_HINTS,
  FACE_LABELS,
  KIND_HINTS,
  KIND_LABELS,
  compact,
  countMatching,
  facetCounts,
  toggle,
} from './model';
import {
  EMAIL_KINDS,
  EXCLUSION_REASONS,
  FACE_SEGMENTS,
  type AudienceFilter,
  type AudienceOrder,
  type AudienceRow,
  type AudienceSummary,
  type EmailKind,
  type EmailStatus,
  type FaceSegment,
} from './types';
import { SendDialog, type SendMode } from './SendDialog';
import { FolderPicker } from './FolderPicker';

const PAGE_SIZES = [
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
] as const;
type PageSize = (typeof PAGE_SIZES)[number]['value'];

const URL_DEFAULTS = {
  kinds: [] as string[],
  faces: [] as string[],
  platforms: [] as string[],
  types: [] as string[],
  folders: [] as string[],
  purpose: '',
  min: '',
  max: '',
  views: '',
  regions: [] as string[],
  named: false,
  q: '',
  size: '50',
  emailed: false,
  noconvo: false,
  noqueue: false,
  page: 1,
};

const ACCOUNT_TYPES = ['personal', 'editor', 'clipper', 'fan_page', 'news_gossip', 'brand_official', 'unknown', 'unclassified'];
const PURPOSES = [
  { value: 'vyral', label: 'Vyral folders' },
  { value: 'clipping', label: 'Clipping folders' },
  { value: 'influencer', label: 'Influencer folders' },
];

const wholeNumber = (raw: string): number | undefined => {
  const n = Number(raw.replace(/[, ]/g, ''));
  return raw.trim() !== '' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
};

/**
 * Who can be emailed. Pick segments, see exactly how many addresses that is
 * and who was left out and why, then export them or send them to Instantly.
 */
export function AudienceTab({ status }: { status: EmailStatus | undefined }): JSX.Element {
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const set = (patch: Partial<typeof URL_DEFAULTS>) => setUrl({ ...patch, page: 1 });
  const folders = useFolders({ includeArchived: true }).data ?? [];
  const [dialog, setDialog] = useState<SendMode | null>(null);
  // Unticked in the preview. Kept with their rows: once out, they no longer come back from the server.
  const [takenOut, setTakenOut] = useState<Map<number, AudienceRow>>(() => new Map());
  // The server takes at most 1,000 by hand; past that, narrow with filters instead.
  const takeOut = (row: AudienceRow) => setTakenOut((m) => (m.size >= 1000 ? m : new Map(m).set(row.creatorId, row)));
  const putBack = (id: number) =>
    setTakenOut((m) => {
      const next = new Map(m);
      next.delete(id);
      return next;
    });
  const pageSize = Number((PAGE_SIZES.find((p) => p.value === url.size) ?? PAGE_SIZES[0]).value);

  const filter: AudienceFilter = useMemo(
    () => ({
      ...(url.q.trim() ? { search: url.q.trim() } : {}),
      ...(url.kinds.length ? { kinds: url.kinds as EmailKind[] } : {}),
      ...(url.faces.length ? { faces: url.faces as FaceSegment[] } : {}),
      ...(url.platforms.length ? { platforms: url.platforms as Platform[] } : {}),
      ...(url.types.length ? { accountTypes: url.types } : {}),
      ...(url.folders.length ? { folders: url.folders } : {}),
      ...(url.purpose ? { purposes: [url.purpose] } : {}),
      ...(wholeNumber(url.min) !== undefined ? { minFollowers: wholeNumber(url.min) } : {}),
      ...(wholeNumber(url.max) !== undefined ? { maxFollowers: wholeNumber(url.max) } : {}),
      ...(wholeNumber(url.views) !== undefined ? { minAvgViews: wholeNumber(url.views) } : {}),
      ...(url.regions.length ? { regions: url.regions } : {}),
      ...(url.named ? { namedOnly: true } : {}),
      ...(takenOut.size ? { excludeCreatorIds: [...takenOut.keys()] } : {}),
      ...(url.emailed ? { includeEmailed: true } : {}),
      ...(url.noconvo ? { includeInConversation: false } : {}),
      ...(url.noqueue ? { includeInQueue: false } : {}),
    }),
    [url.q, url.kinds, url.faces, url.platforms, url.types, url.folders, url.purpose, url.min, url.max, url.views, url.regions, url.named, takenOut, url.emailed, url.noconvo, url.noqueue],
  );

  const order: AudienceOrder = 'followers';
  const summaryQuery = useAudienceSummary(filter);
  const summary = summaryQuery.data;
  const rowsQuery = useAudienceRows(filter, { limit: pageSize, offset: (Math.max(1, url.page) - 1) * pageSize, order });
  const rows = rowsQuery.data?.rows ?? [];

  const cube = summary?.cube ?? [];
  const kindCounts = facetCounts(cube, filter, 'kinds');
  const faceCounts = facetCounts(cube, filter, 'faces');
  const platformCounts = facetCounts(cube, filter, 'platforms');
  const typeCounts = facetCounts(cube, filter, 'accountTypes');
  // From the cube rather than the server's `matching`, so the number moves the instant a tile is clicked.
  const matching = summary ? countMatching(cube, filter) : 0;
  const scoped = !!(url.q || url.folders.length || url.purpose || url.min || url.max || url.views || url.regions.length || url.named);
  const regionCounts = new Map((summary?.regions ?? []).map((r) => [r.region, r.count]));
  const regionOptions = [...new Set([...(summary?.regions ?? []).map((r) => r.region), ...url.regions])].map((code) => ({
    value: code,
    label: `${placeName(code)} (${(regionCounts.get(code) ?? 0).toLocaleString()})`,
  }));
  const folderName = (id: string) => folders.find((f) => f.publicId === id)?.name ?? id;
  const segmented = !!(url.kinds.length || url.faces.length || url.platforms.length || url.types.length);
  const uncheckedShare = summary && summary.eligible > 0 ? (faceCounts.get('unknown') ?? 0) / summary.eligible : 0;

  return (
    <div className="space-y-6">
      {summaryQuery.error && (
        <Note tone="error" role="alert">
          Couldn't load the audience: {(summaryQuery.error as Error).message}
        </Note>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Email type</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {EMAIL_KINDS.map((k) => (
            <SegmentTile
              key={k}
              label={KIND_LABELS[k]}
              hint={KIND_HINTS[k]}
              count={kindCounts.get(k) ?? 0}
              active={url.kinds.includes(k)}
              loading={!summary}
              onClick={() => set({ kinds: toggle(url.kinds, k) })}
            />
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">On camera</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {FACE_SEGMENTS.map((v) => (
            <SegmentTile
              key={v}
              label={FACE_LABELS[v]}
              hint={FACE_HINTS[v]}
              count={faceCounts.get(v) ?? 0}
              active={url.faces.includes(v)}
              loading={!summary}
              onClick={() => set({ faces: toggle(url.faces, v) })}
            />
          ))}
        </div>
        {uncheckedShare > 0.5 && (
          <p className="text-sm text-muted-foreground">
            Most of these creators haven't had the on-camera check, which only runs on searches with the on-camera
            filter switched on. Filtering to "On camera" here will leave most of them out.
          </p>
        )}
      </section>

      <section className="flex flex-wrap items-end gap-3">
        <Field label="Search" className="min-w-[12rem] flex-1">
          <DebouncedInput placeholder="@username, name or email" value={url.q} onCommit={(q) => set({ q })} />
        </Field>
        <Field label="Platform">
          <MultiSelect
            className="w-full md:w-40"
            value={url.platforms}
            onChange={(platforms) => set({ platforms })}
            placeholder="All platforms"
            options={PLATFORMS.map((p) => ({ value: p, label: `${PLATFORM_LABELS[p]} (${(platformCounts.get(p) ?? 0).toLocaleString()})` }))}
          />
        </Field>
        <Field label="Account type">
          <MultiSelect
            className="w-full md:w-44"
            value={url.types}
            onChange={(types) => set({ types })}
            placeholder="Any type"
            options={ACCOUNT_TYPES.filter((t) => (typeCounts.get(t) ?? 0) > 0 || url.types.includes(t)).map((t) => ({
              value: t,
              label: `${ACCOUNT_TYPE_LABELS[t] ?? t} (${(typeCounts.get(t) ?? 0).toLocaleString()})`,
            }))}
          />
        </Field>
        <Field label="Creator country">
          <MultiSelect
            className="w-full md:w-48"
            value={url.regions}
            onChange={(regions) => set({ regions })}
            placeholder="Any country"
            options={regionOptions}
          />
        </Field>
        <Field label="Folder purpose">
          <Combobox className="w-full md:w-44" value={url.purpose} onChange={(purpose) => set({ purpose })} allowAll allLabel="Any purpose" options={PURPOSES} />
        </Field>
        <Field label="Followers">
          <div className="flex items-center gap-1.5">
            <FollowerInput label="Minimum followers" placeholder="Min" value={url.min} onCommit={(min) => set({ min })} />
            <span className="text-muted-foreground">–</span>
            <FollowerInput label="Maximum followers" placeholder="Max" value={url.max} onCommit={(max) => set({ max })} />
          </div>
        </Field>
        <Field label="Average views">
          <FollowerInput label="Minimum average views" placeholder="Min" value={url.views} onCommit={(views) => set({ views })} />
        </Field>
        <div className="pb-2">
          <CheckboxField checked={url.named} onChange={(named) => set({ named })}>
            Real first name only
          </CheckboxField>
        </div>
        {(scoped || segmented) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setUrl({ ...URL_DEFAULTS, emailed: url.emailed, noconvo: url.noconvo, noqueue: url.noqueue, size: url.size })}
          >
            Clear filters
          </Button>
        )}
      </section>
      {url.named && (
        <p className="-mt-3 text-sm text-muted-foreground">
          Real first name only: creators whose display name gives a first name, so emails open "Hey Maya" rather than "Hey
          @maya.grwm". The same rule fills <code className="font-mono text-xs">{'{{greeting_name}}'}</code>.
        </p>
      )}

      <FolderPicker
        folders={summary?.folders ?? []}
        selected={url.folders}
        onChange={(f) => set({ folders: f })}
        loading={!summary}
        nameOf={folderName}
      />

      {summary && <LeftOut summary={summary} url={url} set={set} />}

      <section className="space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Send list</h2>
            <p className="text-sm text-muted-foreground">
              Everyone selected, biggest first. Untick anyone you don't want emailed; they stay out of this send and the
              counts above.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Per page</span>
            <Segmented label="Rows per page" value={url.size as PageSize} onChange={(size) => setUrl({ size, page: 1 })} options={PAGE_SIZES} />
          </div>
        </div>
        {takenOut.size > 0 && (
          <TakenOut rows={[...takenOut.values()]} onPutBack={putBack} onPutAllBack={() => setTakenOut(new Map())} />
        )}
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell className="w-8">
                <span className="sr-only">Send</span>
              </DataTableHeaderCell>
              <DataTableHeaderCell>Creator</DataTableHeaderCell>
              <DataTableHeaderCell>Email</DataTableHeaderCell>
              <DataTableHeaderCell>Type</DataTableHeaderCell>
              <DataTableHeaderCell>On camera</DataTableHeaderCell>
              <DataTableHeaderCell>Account</DataTableHeaderCell>
              <DataTableHeaderCell>Country</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Followers</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Avg views</DataTableHeaderCell>
              <DataTableHeaderCell>Outreach</DataTableHeaderCell>
              <DataTableHeaderCell>Folders</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={11} muted className="py-8 text-center">
                  {rowsQuery.isLoading ? 'Loading…' : 'Nobody matches. Widen the filters, or include a group that is left out above.'}
                </DataTableCell>
              </DataTableRow>
            )}
            {rows.map((r) => (
              <DataTableRow key={r.creatorId}>
                <DataTableCell>
                  <input
                    type="checkbox"
                    aria-label={`Send to @${r.username}`}
                    title="Untick to leave this creator out"
                    className="h-4 w-4 rounded border-input accent-[var(--brand)]"
                    checked
                    onChange={() => takeOut(r)}
                  />
                </DataTableCell>
                <DataTableCell anchor data-label="Creator">
                  <a href={r.profileUrl ?? profileUrl(r.platform, r.username)} target="_blank" rel="noreferrer" className="hover:underline">
                    @{r.username}
                  </a>
                  <span className="ml-2 text-sm font-normal text-muted-foreground">{PLATFORM_LABELS[r.platform]}</span>
                </DataTableCell>
                <DataTableCell data-label="Email">{r.email}</DataTableCell>
                <DataTableCell data-label="Type">{KIND_LABELS[r.kind]}</DataTableCell>
                <DataTableCell data-label="On camera" muted={r.face === 'unknown'}>{FACE_LABELS[r.face]}</DataTableCell>
                <DataTableCell data-label="Account" muted={!r.accountType}>
                  {r.accountType ? (ACCOUNT_TYPE_LABELS[r.accountType] ?? r.accountType) : '—'}
                </DataTableCell>
                <DataTableCell data-label="Country" muted={!r.region} title={r.region ?? undefined}>
                  {r.region ? placeName(r.region) : '—'}
                </DataTableCell>
                <DataTableCell numeric data-label="Followers">{compact(r.followers)}</DataTableCell>
                <DataTableCell numeric data-label="Avg views">{compact(r.avgViews)}</DataTableCell>
                <DataTableCell data-label="Outreach">
                  <OutreachStatusBadge creator={{ outreachState: r.outreachState as OutreachState }} hideNone />
                </DataTableCell>
                <DataTableCell data-label="Folders" muted className="max-w-[18rem] truncate" title={r.folders.join(', ')}>
                  {r.folders.join(', ') || '—'}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
        <Pagination page={Math.max(1, url.page)} limit={pageSize} total={matching} onPageChange={(page) => setUrl({ page })} />
      </section>

      {/* Last in the page so it stays pinned to the bottom the whole way down. */}
      <section className="panel sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3">
        <div>
          <div className="text-base font-semibold">
            <span className="font-mono tabular-nums">{summary ? matching.toLocaleString() : '…'}</span>{' '}
            {matching === 1 ? 'address' : 'addresses'} selected
          </div>
          <div className="text-sm text-muted-foreground">
            {summary
              ? `${
                  segmented || scoped ? `of ${summary.eligible.toLocaleString()} you can email with these filters` : 'everyone you can email right now'
                }${takenOut.size ? ` · ${takenOut.size.toLocaleString()} taken out by hand` : ''}`
              : 'Counting…'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" disabled={matching === 0} onClick={() => setDialog('csv')}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            type="button"
            disabled={matching === 0 || !status?.instantly.connected}
            title={status?.instantly.connected ? undefined : 'Instantly is not connected yet'}
            onClick={() => setDialog('instantly')}
          >
            <Send className="h-4 w-4" />
            Send to Instantly
          </Button>
        </div>
      </section>

      {dialog && summary && (
        <SendDialog
          mode={dialog}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          filter={filter}
          matching={matching}
          maxBatch={status?.maxBatch ?? 5000}
          linkBase={status?.linkTracking?.base ?? null}
          folderName={folderName}
        />
      )}
    </div>
  );
}

function SegmentTile({
  label,
  hint,
  count,
  active,
  loading,
  onClick,
}: {
  label: string;
  hint: string;
  count: number;
  active: boolean;
  loading: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn('panel rounded-xl px-4 py-3 text-left transition-colors', active ? 'border-brand/50 bg-brand/10' : 'hover:bg-accent/40')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold">{label}</span>
        <span className={cn('font-mono text-xl font-semibold tabular-nums', active && 'text-brand')}>
          {loading ? '…' : count.toLocaleString()}
        </span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
    </button>
  );
}

function FollowerInput({ label, placeholder, value, onCommit }: { label: string; placeholder: string; value: string; onCommit: (v: string) => void }): JSX.Element {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    // An outside change (Clear filters) wins over a stale draft.
    setSeen(value);
    setDraft(value);
  }
  const commit = () => draft !== value && onCommit(wholeNumber(draft) !== undefined ? String(wholeNumber(draft)) : '');
  return (
    <Input
      aria-label={label}
      inputMode="numeric"
      className="w-24"
      placeholder={placeholder}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => ev.key === 'Enter' && commit()}
    />
  );
}

/**
 * Everyone with an email who is NOT in the audience, and why. Shown rather
 * than hidden so the selected count can always be reconciled with the
 * Creators page, and so the two groups that are a judgement call can be
 * switched back in.
 */
function LeftOut({ summary, url, set }: { summary: AudienceSummary; url: typeof URL_DEFAULTS; set: (patch: Partial<typeof URL_DEFAULTS>) => void }): JSX.Element {
  const reasons = EXCLUSION_REASONS.filter((r) => summary.excluded[r] > 0);
  const leftOut = summary.withEmail - summary.eligible;
  return (
    <section className="panel space-y-3 rounded-xl px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Left out</h2>
        <p className="text-sm text-muted-foreground">
          {summary.withEmail.toLocaleString()} creators have an email · {summary.eligible.toLocaleString()} can be emailed ·{' '}
          {leftOut.toLocaleString()} left out
        </p>
      </div>
      {leftOut === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody. Every creator with an email in these filters can be emailed.</p>
      ) : (
        <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {reasons.map((r) => (
            <li key={r} className="flex justify-between gap-3">
              <span>{EXCLUSION_LABELS[r]}</span>
              <span className="font-mono tabular-nums text-muted-foreground">{summary.excluded[r].toLocaleString()}</span>
            </li>
          ))}
          {summary.sharedAddress > 0 && (
            <li className="flex justify-between gap-3">
              <span>Share an address with a bigger creator</span>
              <span className="font-mono tabular-nums text-muted-foreground">{summary.sharedAddress.toLocaleString()}</span>
            </li>
          )}
        </ul>
      )}
      <div className="grid gap-2 border-t border-border pt-3 sm:grid-cols-2">
        <CheckboxField
          checked={!url.noconvo}
          onChange={(v) => set({ noconvo: !v })}
          hint="They're mid-conversation by DM. Untick to leave them out, so an email doesn't blur who gets credit."
        >
          Include creators an outreacher has messaged or logged
        </CheckboxField>
        <CheckboxField
          checked={!url.noqueue}
          onChange={(v) => set({ noqueue: !v })}
          hint="They're waiting in an outreacher's DM queue and haven't been messaged. Untick to leave them to the outreacher."
        >
          Include creators in the DM queue who haven't been messaged
        </CheckboxField>
        <CheckboxField checked={url.emailed} onChange={(v) => set({ emailed: v })} hint="They could get a second email from a different campaign.">
          Include creators already in an email campaign
        </CheckboxField>
      </div>
    </section>
  );
}

/** Creators unticked in the send list, with a way back in. */
function TakenOut({ rows, onPutBack, onPutAllBack }: { rows: AudienceRow[]; onPutBack: (id: number) => void; onPutAllBack: () => void }): JSX.Element {
  return (
    <div className="panel flex flex-wrap items-center gap-2 rounded-xl px-4 py-3 text-sm">
      <span className="font-medium">
        {rows.length.toLocaleString()} taken out by hand:
      </span>
      {rows.slice(0, 30).map((r) => (
        <button
          key={r.creatorId}
          type="button"
          className="rounded-md border border-border px-2 py-0.5 hover:bg-accent/40"
          title="Put back in the send list"
          onClick={() => onPutBack(r.creatorId)}
        >
          @{r.username} ×
        </button>
      ))}
      {rows.length > 30 && <span className="text-muted-foreground">and {(rows.length - 30).toLocaleString()} more</span>}
      <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={onPutAllBack}>
        Put all back
      </Button>
    </div>
  );
}
